import React from 'react';
import axios from 'axios';
import { getDecodedQueryParams, encodedQueryParams } from '../utils/url';
import { SORT_BY_HIGH_TO_LOW, API } from '../utils/constants';

const { Provider, Consumer } = React.createContext({});

export const RICH_FILTERING_STATE = {
  SUCCESS: 'SUCCESS',
  LOADING: 'LOADING',
  INITIAL: 'INITIAL',
  ERROR: 'ERROR',
};

/**
 * Sorts list of locations, setting and the end of the list the locations with
 * no price.
 * @param {array} locations
 * @returns {array} locations which priced are first and unpriced last
 */
export const sortByCallUs = locations => {
  const { priced, unpriced } = locations.reduce(
    (acc, location) => {
      const filteredLocations = { ...acc };

      if (location.Solution.Price) {
        filteredLocations.priced.push(location);
      } else {
        filteredLocations.unpriced.push(location);
      }

      return filteredLocations;
    },
    { priced: [], unpriced: [] },
  );

  return [...priced, ...unpriced];
};

class RichFilteringProvider extends React.Component {
  state = {
    brands: [],
    brandsNumbers: {},
    hoverElement: '',
    isFilterModalOpen: false,
    locations: [],
    locationsFromAPI: [],
    page: 1,
    params: {},
    richFilteringState: RICH_FILTERING_STATE.LOADING,
    showMap: false,
    sort: '',
    totalAmount: 0,
    totalPages: 1,
    viewPerPages: 20,
  };

  /**
   * delete from the params object all the empty values to avoid
   * set the window url with empty values
   * @param {Object} params
   * @return {Object} received params but empty values deleted
   * @example
   * // when the function receive a param object like param={a: 'value', b: 'value', c: ''}
   * // it will return param={a: 'value', b: 'value'}
   */
  deleteEmptyParams = params => {
    Object.keys(params).forEach(param => {
      if (!params[param]) {
        delete params[param];
      }
    });

    return params;
  };

  /**
   * This function
   * @param {Object} queryParams
   */
  replaceUrlParams = queryParams => {
    let params = getDecodedQueryParams();

    Object.keys(queryParams).forEach(param => {
      params[param] =
        queryParams[param] !== undefined ? queryParams[param] : params[param];
    });
    params = this.deleteEmptyParams(params);

    const encodedParams = encodedQueryParams(params);

    return encodedParams;
  };

  /**
   * This function replace the url with the upcoming params
   * without reloading the browser
   * @param {Object} queryParams
   * @param  {string} queryParams.ws
   * @param  {string} queryParams.southwest
   * @param  {string} queryParams.northeast
   * @param  {string} queryParams.lang
   */
  replaceWindowUrl = queryParams => {
    const { origin, pathname } = window.location;
    const params = this.replaceUrlParams(queryParams);
    const newUrl = `${origin}${pathname}${params}`;

    window.history.pushState({}, null, newUrl);
  };

  getBrandsQuantity = locations =>
    locations.reduce((acc, curr) => {
      const brandName = curr.Location.Brand;
      return { ...acc, [brandName]: acc[brandName] ? acc[brandName] + 1 : 1 };
    }, {});

  /**
   * Get the information from the API(endpoint geo results) and store
   * the data in the locations state of the rich filtering provider
   * @param {Object} queryParams
   * @param  {string} queryParams.ws
   * @param  {string} queryParams.southwest
   * @param  {string} queryParams.northeast
   * @param  {string} queryParams.lang
   */
  getApiData = queryParams => {
    const params = this.replaceUrlParams(queryParams);
    const buildUrl = `${API.GEO_RESULTS}${params}`;

    this.setState({
      richFilteringState: RICH_FILTERING_STATE.LOADING,
    });

    //TODO: move this axios call to the services folder
    axios
      .get(buildUrl)
      .then(({ data }) => {
        const { viewPerPages } = this.state;
        const totalAmount = data.length;
        const totalPages = Math.ceil(totalAmount / viewPerPages);
        const decodedParams = getDecodedQueryParams();
        const locations = this.applyFilters(data, decodedParams);
        const brandsNumbers = this.getBrandsQuantity(data);

        this.setState({
          brandsNumbers,
          locations,
          locationsFromAPI: data,
          page: 1,
          richFilteringState: RICH_FILTERING_STATE.SUCCESS,
          totalAmount,
          totalPages,
        });
      })
      .catch(() => {
        this.setState({
          richFilteringState: RICH_FILTERING_STATE.ERROR,
          locationsFromAPI: [],
          locations: [],
          brandsNumbers: {},
          totalAmount: 0,
          totalPages: 1,
          page: 1,
        });
      });
  };

  filterByBrands = (locations, brands) => {
    if (!brands || brands.length === 0) return locations;

    return locations.filter(
      location =>
        location.Location.Brand && brands.indexOf(location.Location.Brand) > -1,
    );
  };

  sortByPrice = (locations, sort) => {
    if (sort === SORT_BY_HIGH_TO_LOW) {
      return locations.sort((a, b) => b.Solution.Price - a.Solution.Price);
    }

    return locations.sort((a, b) => a.Solution.Price - b.Solution.Price);
  };

  inRange = (locations, minInt, maxInt) => {
    const [minRange, maxRange] = [minInt, maxInt].sort((a, b) => a - b);

    return locations.filter(
      location =>
        location.Solution.Price >= minRange &&
        location.Solution.Price <= maxRange,
    );
  };

  cleanCallUsLocations = locations => {
    return locations.filter(location => location.Solution.Price !== 0);
  };

  filterByRange = (locations, min, max) => {
    const minInt = Number(min);
    const maxInt = Number(max);

    if (!maxInt && !minInt) return locations;

    const locationsWithoutCallUs = this.cleanCallUsLocations(locations);

    if (maxInt > 0 && minInt > 0) {
      return this.inRange(locationsWithoutCallUs, minInt, maxInt);
    }

    if (maxInt > 0) {
      return locationsWithoutCallUs.filter(
        location => location.Solution.Price <= maxInt,
      );
    }

    return locationsWithoutCallUs.filter(
      location => location.Solution.Price >= minInt,
    );
  };

  applyFilters = (locations, filters) => {
    const { brands, sort, min, max } = filters;
    let filteredLocations = this.filterByBrands(locations, brands);
    filteredLocations = this.sortByPrice(filteredLocations, sort);
    filteredLocations = this.filterByRange(filteredLocations, min, max);
    filteredLocations = sortByCallUs(filteredLocations);

    return filteredLocations;
  };

  filterLocations = filters => {
    const { locationsFromAPI } = this.state;
    const locations = this.applyFilters(locationsFromAPI, filters);

    this.setState({ locations });
  };

  storeUrlParams = () => {
    const params = getDecodedQueryParams();
    this.setState({ params });
  };

  toggleMapVisibility = showMap => {
    this.setState({
      showMap: showMap === undefined ? !this.state.showMap : showMap,
    });
  };

  onHoverCard = event => {
    const { type, target } = event;

    switch (type) {
      case 'mouseover':
      case 'focus':
        const { id } = target.dataset;
        this.setState({ hoverElement: id });
        return;
      case 'mouseout':
      case 'blur':
        this.setState({ hoverElement: '' });
        return;

      default:
        return;
    }
  };

  goNext = () => {
    const { page } = this.state;
    this.setState({
      page: page + 1,
    });
  };

  closeModal = () => {
    this.setState({ isFilterModalOpen: false });
  };

  openModal = () => {
    this.setState({ isFilterModalOpen: true });
  };

  actions = {
    replaceWindowUrl: this.replaceWindowUrl,
    getApiData: this.getApiData,
    filterLocations: this.filterLocations,
    storeUrlParams: this.storeUrlParams,
    toggleMapVisibility: this.toggleMapVisibility,
    onHoverCard: this.onHoverCard,
    goNext: this.goNext,
    closeModal: this.closeModal,
    openModal: this.openModal,
  };

  render() {
    return (
      <Provider
        value={{
          state: this.state,
          actions: this.actions,
        }}
      >
        {this.props.children}
      </Provider>
    );
  }
}

export { RichFilteringProvider, Consumer as RichFilteringConsumer };
